/*
 * Copyright © 2020-2026 Ocean Galaxy Inc. All Rights Reserved.
 * @Description:
 * @LastEditors: yongqiang
 * @LastEditTime: 2026-01-13 14:55:39
 */

import CryptoJS from 'crypto-js'
import JSEncrypt from 'jsencrypt'
/**
 * 加密后的数据结构接口
 */
interface EncryptedResult {
  ciphertext: string // 密文
  iv: string // 偏移量
}

/**
 * AES 对称加解密工具类 (使用 AES-256-CBC 模式)
 */
export class AESHelper {
  /**
   * 生成随机 AES 密钥 (32字符/256位)
   */
  static generateKey(): string {
    return CryptoJS.lib.WordArray.random(32).toString(CryptoJS.enc.Hex)
  }

  /**
   * AES 加密
   * @param data 待加密的内容（对象或字符串）
   * @param key 16/24/32位密钥
   */
  static encrypt(data: any, key: string): EncryptedResult {
    const text = typeof data === 'object' ? JSON.stringify(data) : String(data)
    const hexKey = CryptoJS.enc.Hex.parse(key)
    // 生成随机 IV
    const iv = CryptoJS.lib.WordArray.random(16)

    const encrypted = CryptoJS.AES.encrypt(text, hexKey, {
      iv: iv,
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7
    })

    return {
      ciphertext: encrypted.toString(),
      iv: iv.toString(CryptoJS.enc.Base64)
    }
  }

  /**
   * AES 解密
   * @param ciphertext 密文
   * @param key 密钥
   * @param iv 偏移量
   */
  static decrypt(ciphertext: string, key: string, iv: string): string {
    const hexKey = CryptoJS.enc.Hex.parse(key)
    const ivBase64 = CryptoJS.enc.Base64.parse(iv)

    const decrypted = CryptoJS.AES.decrypt(ciphertext, hexKey, {
      iv: ivBase64,
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7
    })

    return decrypted.toString(CryptoJS.enc.Utf8)
  }
}

/**
 * RSA 非对称加密工具类 (用于加密 AES Key)
 */
export class RSAHelper {
  /**
   * 使用 RSA 公钥加密文本
   * @param text 待加密的短文本 (如 AES Key)
   * @param publicKey PEM 格式的公钥
   */
  static encrypt(text: string, publicKey: string): string {
    const jsEncrypt = new JSEncrypt()
    jsEncrypt.setPublicKey(publicKey)
    const encrypted = jsEncrypt.encrypt(text)
    if (!encrypted) {
      throw new Error('RSA 加密失败：请检查公钥格式或数据长度')
    }
    return encrypted
  }
}
